<template>
  <v-container
    v-if="loading"
    class="my-3"
  >   
    <v-row>
      <v-col cols="12">
        <v-skeleton-loader
          color="grey lighten-2"
          class="mx-auto"
          type="table-tbody"
        />
      </v-col>
    </v-row>
  </v-container>

  <v-card
    v-else
    class="py-0 ma-0"
  >
    <v-toolbar flat>
      <v-toolbar-title>Leave Entitlements</v-toolbar-title>
    </v-toolbar>
    <v-simple-table
      class="px-4 pb-3"
    >
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left">
              <span class="font-weight-bold">Leave Type</span>
            </th>
            <th class="text-left">
              <span class="font-weight-bold">Year</span>
            </th>
            <th class="text-right">
              <span class="font-weight-bold">Brought Forward</span>
            </th>
            <th class="text-right">
              <span class="font-weight-bold">Current Available</span>
            </th>
            <th class="text-right">
              <span class="font-weight-bold">Pending Application</span>
            </th>
            <th class="text-right">
              <span class="font-weight-bold">Leave Utilized</span>
            </th>
            <th class="text-right">
              <span class="font-weight-bold">Leave Balance</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, i) in leaveEntitlements"
            :key="i"
            :index="i"
            :count="leaveEntitlements.length"
          >
            <td>
              {{ item.leave_type.name }}
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-icon
                    v-if="identifyAvailableLeaveType(item.leave_type.id) > 0"
                    color="red"
                    v-on="on"
                  >
                    mdi-lock
                  </v-icon>
                </template>
                Leave locked
              </v-tooltip>
            </td>
            <td>{{ item.year }}</td>
            <td class="text-right">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-icon
                    v-if="item.leave_type.is_carry_forwardable && item.carried_forward_days"
                    color="blue"
                    v-on="on"
                  >
                    mdi-information-outline
                  </v-icon>
                </template>
                Max Carry Forward Days : {{ item.maximum_carry_forward_amount ? parseFloat(item.maximum_carry_forward_amount).toFixed(2) : '0.00' }}
              </v-tooltip>
              {{ item.leave_type.name === 'UNPAID LEAVE' ? '0.00' : (item.carried_forward_days ? parseFloat(item.carried_forward_days).toFixed(2) : '0.00') }}             
            </td>
            <td class="text-right">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-icon
                    v-if="item.leave_type.is_prorated || item.leave_type.is_bi_yearly"
                    color="blue"
                    v-on="on"
                  >
                    mdi-information-outline
                  </v-icon>
                </template>
                Entitlement Days : {{ parseFloat(item.entitled_days).toFixed(2) }}
              </v-tooltip>
              <v-icon v-if="item.leave_type.name === 'UNPAID LEAVE'">
                mdi-infinity
              </v-icon>
              <span v-else>{{ (parseFloat(item.entitled_days_taken || 0) + parseFloat(item.carried_forward_days_taken || 0) + parseFloat(item.entitlement_days || 0)).toFixed(2) }}</span>  
            </td>
            <td class="text-right">
              {{ (parseFloat(item.pendingApplications || 0)).toFixed(2) }}
            </td>
            <td class="text-right">
              {{ (parseFloat(item.entitled_days_taken || 0) + parseFloat(item.carried_forward_days_taken || 0)).toFixed(2) }}
            </td>
            <td class="text-right">
              <v-icon v-if="item.leave_type.name === 'UNPAID LEAVE'">
                mdi-infinity
              </v-icon>
              <span v-else>{{ item.available_days ? (item.available_days - item.pendingApplications > 0 ? (parseFloat(item.available_days) - item.pendingApplications).toFixed(2) : '0.00') : '0.00' }}</span> 
            </td>
          </tr>
        </tbody> 
      </template>
    </v-simple-table>
  </v-card>
</template>

<script>
  export default {
    props: {
      employeeUid: {
        type: String,
        default: '',
      },
    },
    data () {
      return {
        menu: [],
        leaveEntitlements: [],
        loading: false,
        leaveLockSettings: [],
        employeeData: null,
      }
    },
    watch: {
      employeeUid: {
        immediate: true,
        handler (value) {
          if (value) {
            this.loadData()
          }
        },
      },
    },
    methods: {
      identifyAvailableLeaveType (selectedLeaveTypeId) {
        return this.leaveLockSettings.filter(lock => {
          return lock.leave_type_id === selectedLeaveTypeId && (lock.start_date <= this.$moment().format('YYYY-MM-DD') && lock.end_date >= this.$moment().format('YYYY-MM-DD'))
        }).length
      },
      loadData () {
        this.loading = true
        Promise.all([
          this.$api.leaveApplications().list(this.employeeUid),
          this.$api.leaveLockSettings().yearFilteredList((new Date()).getFullYear()),
          this.$api.hrisEmployees().show(this.employeeUid),
        ]).then(([leaveApplication, leaveLockSetting, employeeDetail]) => {
          leaveApplication.data.map(item => {
            item.entitlement_days = item.available_days
            item.pendingApplications = this.getPendingApplications(item.leave_applications)
          })

          this.employeeData = employeeDetail.data
          this.leaveLockSettings = leaveLockSetting.data
    
          let annualLeave = leaveApplication.data.find(entitlement => entitlement.leave_type_id === 1) // annual leave
          let unpaidLeave = leaveApplication.data.find(entitlement => entitlement.leave_type_id === 3) // unpaid leave
          let sickLeave = leaveApplication.data.find(entitlement => entitlement.leave_type_id === 2) // sick leave
          let unpaidSickLeave = leaveApplication.data.find(entitlement => entitlement.leave_type_id === 17) // unpaid sick leave
          let covidPaidTimeOffLeave = leaveApplication.data.find(entitlement => entitlement.leave_type_id === 10) // covid paid time off leave
          let hospitalizationLeave = leaveApplication.data.find(entitlement => entitlement.leave_type_id === 11) // hospitalization leave
          let birthdayLeave = leaveApplication.data.find(entitlement => entitlement.leave_type_id === 19) // birthday leave
          let compassionateLeave = leaveApplication.data.find(entitlement => entitlement.leave_type_id === 6) // compassionate leave
          let marriageLeave = leaveApplication.data.find(entitlement => entitlement.leave_type_id === 7) // marriage leave
          let naturalDisasterLeave = leaveApplication.data.find(entitlement => entitlement.leave_type_id === 9) // natural disaster leave
          let paternityLeave = leaveApplication.data.find(entitlement => entitlement.leave_type_id === 4) // paternity leave
          let prolongLeave = leaveApplication.data.find(entitlement => entitlement.leave_type_id === 12) // prolong medical disaster leave
          let examinationLeave = leaveApplication.data.find(entitlement => entitlement.leave_type_id === 8) // examination leave

          this.leaveEntitlements = leaveApplication.data.filter(entitlement => {
            if (annualLeave && annualLeave.leave_type && entitlement.leave_type_id === annualLeave.leave_type.id) {
              if (this.$moment(this.employeeData.confirmed_date).format('YYYY-MM-DD') <= this.$moment().format('YYYY-MM-DD') && annualLeave.available_days >= 0.5) {
                return entitlement
              }
            } else if (unpaidLeave && unpaidLeave.leave_type && entitlement.leave_type_id === unpaidLeave.leave_type.id && (annualLeave && this.$moment(annualLeave.start_date).format('YYYY-MM-DD') <= this.$moment().format('YYYY-MM-DD'))) {
              if (annualLeave && annualLeave.year === entitlement.year) {
                if (annualLeave.available_days < 0.5) {
                  return entitlement
                }
              } else {
                return entitlement
              }
            } else if (sickLeave && sickLeave.leave_type && entitlement.leave_type_id === sickLeave.leave_type.id) {
              if (sickLeave.available_days >= 0) {
                return entitlement
              }
            } else if (unpaidSickLeave && unpaidSickLeave.leave_type && entitlement.leave_type_id === unpaidSickLeave.leave_type.id) {
              if (sickLeave) {
                return entitlement
              } else {
                if (sickLeave.available_days < 0.5) {
                  return entitlement
                }
              }
            } else if (covidPaidTimeOffLeave && covidPaidTimeOffLeave.leave_type && entitlement.leave_type_id === covidPaidTimeOffLeave.leave_type.id) {
              if (covidPaidTimeOffLeave.entitled_days >= 0) {
                covidPaidTimeOffLeave.entitlement_days = 5 - covidPaidTimeOffLeave.entitled_days_taken
                if (covidPaidTimeOffLeave.entitled_days_taken > 5) {
                  hospitalizationLeave.entitlement_days = hospitalizationLeave.entitled_days - covidPaidTimeOffLeave.entitled_days_taken - hospitalizationLeave.entitled_days_taken
                } else {
                  hospitalizationLeave.entitlement_days = hospitalizationLeave.entitled_days - 5 - hospitalizationLeave.entitled_days_taken
                }
              }
              covidPaidTimeOffLeave.available_days = covidPaidTimeOffLeave.entitlement_days
              hospitalizationLeave.available_days = hospitalizationLeave.entitlement_days

              return entitlement
            } else if (birthdayLeave && birthdayLeave.leave_type && entitlement.leave_type_id === birthdayLeave.leave_type.id) {
              if (this.$moment(birthdayLeave.start_date).format('YYYY-MM-DD') <= this.$moment().format('YYYY-MM-DD')) {
                return entitlement
              }
            } else if (compassionateLeave && compassionateLeave.leave_type && entitlement.leave_type_id === compassionateLeave.leave_type.id) {
              if (this.$moment(compassionateLeave.start_date).format('YYYY-MM-DD') <= this.$moment().format('YYYY-MM-DD')) {
                return entitlement
              }
            } else if (marriageLeave && marriageLeave.leave_type && entitlement.leave_type_id === marriageLeave.leave_type.id) {
              if (this.$moment(marriageLeave.start_date).format('YYYY-MM-DD') <= this.$moment().format('YYYY-MM-DD')) {
                return entitlement
              }
            } else if (naturalDisasterLeave && naturalDisasterLeave.leave_type && entitlement.leave_type_id === naturalDisasterLeave.leave_type.id) {
              if (this.$moment(naturalDisasterLeave.start_date).format('YYYY-MM-DD') <= this.$moment().format('YYYY-MM-DD')) {
                return entitlement
              }        
            } else if (paternityLeave && paternityLeave.leave_type && entitlement.leave_type_id === paternityLeave.leave_type.id) {
              if (this.$moment(paternityLeave.start_date).format('YYYY-MM-DD') <= this.$moment().format('YYYY-MM-DD')) {
                return entitlement
              }   
            } else if (prolongLeave && prolongLeave.leave_type && entitlement.leave_type_id === prolongLeave.leave_type.id) {
              if (this.$moment(prolongLeave.start_date).format('YYYY-MM-DD') <= this.$moment().format('YYYY-MM-DD')) {
                return entitlement
              } 
            } else if (examinationLeave && examinationLeave.leave_type && entitlement.leave_type_id === examinationLeave.leave_type.id) {
              if (this.$moment(examinationLeave.start_date).format('YYYY-MM-DD') <= this.$moment().format('YYYY-MM-DD')) {
                return entitlement
              }                                                                             
            } else {
              return entitlement
            }
          }).filter(entitlement => {
            return entitlement.leave_type.is_apply_on_behalf === 0
          })

          this.leaveEntitlements.sort((a, b) => {
            return a.leave_type.name > b.leave_type.name ? 1 : -1
          })

          this.loading = false
        })
      },

      getPendingApplications (leaveApplications) {
        let sum = 0
        leaveApplications.map(application => {
          if (application.approval_request.approvals[application.approval_request.approvals.length - 1].status.id === 100) {
            sum = sum + application.requested_days
          }
        })

        return sum
      },
    },
  }
</script>
